import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Çerez onayı (KVKK / GDPR).
 *
 * Google Ads + GA4 etiketleri (public/index.html → window.paydaaiLoadTags)
 * yalnızca kullanıcı "Kabul et" dediğinde yüklenir. Seçim, paydaai.com ile
 * ortak olsun diye `.paydaai.com` alan adlı `paydaai_consent` çerezinde
 * tutulur (değer: granted | denied, 180 gün).
 */
export const CONSENT_COOKIE = 'paydaai_consent';
const MAX_AGE = 60 * 60 * 24 * 180;
export const OPEN_EVENT = 'paydaai:cookie-preferences';

const TEXT = {
  tr: {
    body: 'Zorunlu çerezler dışında, reklamlarımızın ve sitemizin performansını ölçmek için Google Analytics ve Google Ads çerezlerini yalnızca izin verirsen kullanırız.',
    accept: 'Kabul et', reject: 'Reddet', policy: 'Çerez Politikası',
    href: 'https://paydaai.com/cerez-politikasi',
  },
  en: {
    body: 'Apart from essential cookies, we only use Google Analytics and Google Ads cookies to measure our ads and site performance if you allow it.',
    accept: 'Accept', reject: 'Reject', policy: 'Cookie Policy',
    href: 'https://paydaai.com/en/cookie-policy',
  },
  de: {
    body: 'Außer notwendigen Cookies verwenden wir Google-Analytics- und Google-Ads-Cookies zur Messung unserer Werbung und Website-Leistung nur mit deiner Zustimmung.',
    accept: 'Akzeptieren', reject: 'Ablehnen', policy: 'Cookie-Richtlinie',
    href: 'https://paydaai.com/en/cookie-policy',
  },
  fr: {
    body: 'En dehors des cookies essentiels, nous n’utilisons les cookies Google Analytics et Google Ads pour mesurer nos publicités et les performances du site qu’avec votre accord.',
    accept: 'Accepter', reject: 'Refuser', policy: 'Politique de cookies',
    href: 'https://paydaai.com/en/cookie-policy',
  },
  es: {
    body: 'Además de las cookies esenciales, solo usamos cookies de Google Analytics y Google Ads para medir nuestros anuncios y el rendimiento del sitio si lo permites.',
    accept: 'Aceptar', reject: 'Rechazar', policy: 'Política de cookies',
    href: 'https://paydaai.com/en/cookie-policy',
  },
};

export function readConsent() {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp('(?:^|; )' + CONSENT_COOKIE + '=([^;]+)'));
  return m ? decodeURIComponent(m[1]) : null;
}

function cookieDomain() {
  const h = window.location.hostname;
  return h === 'paydaai.com' || h.endsWith('.paydaai.com') ? '; domain=.paydaai.com' : '';
}

// Reddedilince daha önce yazılmış Google çerezlerini temizle.
function clearTrackingCookies() {
  const names = document.cookie.split('; ').map((c) => c.split('=')[0]);
  const domains = ['', '; domain=.paydaai.com', `; domain=${window.location.hostname}`];
  names.filter((n) => /^(_ga|_gid|_gat|_gcl_)/.test(n)).forEach((n) => {
    domains.forEach((d) => { document.cookie = `${n}=; max-age=0; path=/${d}`; });
  });
}

export function saveConsent(value) {
  document.cookie = `${CONSENT_COOKIE}=${value}; max-age=${MAX_AGE}; path=/; SameSite=Lax${cookieDomain()}${window.location.protocol === 'https:' ? '; Secure' : ''}`;
  if (value === 'granted') {
    if (typeof window.paydaaiLoadTags === 'function') window.paydaaiLoadTags();
  } else {
    clearTrackingCookies();
  }
}

export function openCookiePreferences() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function CookieConsent() {
  const { i18n } = useTranslation();
  const [visible, setVisible] = useState(false);
  const lang = String(i18n.resolvedLanguage || i18n.language || 'tr').slice(0, 2);
  const T = TEXT[lang] || TEXT.en;

  useEffect(() => {
    if (!readConsent()) setVisible(true);
    const open = () => setVisible(true);
    window.addEventListener(OPEN_EVENT, open);
    return () => window.removeEventListener(OPEN_EVENT, open);
  }, []);

  if (!visible) return null;

  const choose = (value) => {
    const wasGranted = readConsent() === 'granted';
    saveConsent(value);
    setVisible(false);
    // Etiketler yüklendikten sonra geri çekilirse betikleri kaldırmanın tek yolu sayfayı yenilemek.
    if (wasGranted && value === 'denied') window.location.reload();
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={T.policy}
      data-testid="cookie-consent"
      className="fixed inset-x-0 bottom-0 z-[100] border-t border-slate-200 bg-white p-4 shadow-lg"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-600">
          {T.body}{' '}
          <a href={T.href} target="_blank" rel="noreferrer" className="underline">{T.policy}</a>
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => choose('denied')}
            data-testid="cookie-reject"
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
          >
            {T.reject}
          </button>
          <button
            type="button"
            onClick={() => choose('granted')}
            data-testid="cookie-accept"
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
          >
            {T.accept}
          </button>
        </div>
      </div>
    </div>
  );
}
